import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Database, onDisconnect, ref, set } from "firebase/database";
import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  query,
  runTransaction,
  updateDoc,
  where,
  serverTimestamp,
} from "firebase/firestore";

import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { Adsense } from "@ctrl/react-adsense";
import { setShowFriends, setShowNavbar } from "stores/uiState/UiStateSlice";
import { defaultStartingElo } from "constants/gameDefaults";
import { AdsenseAccount } from "constants/config";
import { RootState } from "stores/Store";
import { setGame } from "stores/game/GameSlice";
import { setLobby } from "stores/lobby/LobbySlice";
import { Lobby, getNewLobby } from "interfaces/lobby";
import {
  Game,
  getNewGame,
  GameModeEnum,
  GameStateEnum,
  GameStatusEnum,
} from "interfaces/game";
import Login from "components/Login";
import joinIcon from "styles/images/join.svg";
import { showAds } from "utils/ads";

const ELODifference = 200;

interface CreateOrJoinGameProps {
  RTDB: Database;
}

function CreateOrJoinGame({ RTDB }: CreateOrJoinGameProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(setShowNavbar(true));
    dispatch(setShowFriends(true));
  }, []);

  const user = useSelector(
    (state: RootState) => state.userReducer.user,
    shallowEqual,
  );

  useEffect(() => {
    const fetchEloHistory = async () => {
      const db = getFirestore();
      if (user) {
        const userEloDoc = doc(db, "users", user.uid);
        const userEloSnapshot = await getDoc(userEloDoc);
        if (userEloSnapshot.exists()) {
          // setEloHistoryData(userEloSnapshot.data().eloHistory);
        } else {
          console.error("No Elo History found for user");
        }
      } else {
        console.error("User is undefined.");
      }
    };
    if (user) {
      fetchEloHistory();
    }
  }, [user]);

  const createGame = async (mode: GameModeEnum): Promise<Game | null> => {
    try {
      const db = getFirestore();
      const newGame: Game = getNewGame(user, mode);
      const newGameRef = await addDoc(collection(db, "games"), newGame);

      dispatch(setGame({ ...newGame, id: newGameRef.id }));

      return { ...newGame, id: newGameRef.id };
    } catch (error) {
      console.error("Error creating game: ", error);
    }

    return null;
  };

  const createLobby = async (mode: GameModeEnum): Promise<Lobby | null> => {
    try {
      if (!user) {
        throw new Error("User is null");
      }

      const newGame = await createGame(mode);
      const newLobby = getNewLobby(user, newGame);

      const db = getFirestore();

      const newLobbyDoc = await addDoc(collection(db, "lobbies"), newLobby);
      if (newGame?.id) {
        await updateDoc(doc(db, "games", newGame.id), {
          lobbId: newLobbyDoc.id,
          timestamp: serverTimestamp(),
        });
      }

      const lobbyRef = ref(RTDB, `lobbies-activity/${newLobbyDoc.id}`);
      set(lobbyRef, {
        status: GameStatusEnum.CONNECTED,
        lobbyId: newLobbyDoc.id,
        userId: user.uid,
      });

      onDisconnect(lobbyRef).set({
        status: GameStatusEnum.DISCONNECTED,
      });

      const lobbyWithId: Lobby = {
        ...newLobby,
        id: newLobbyDoc.id,
      };

      dispatch(setLobby(lobbyWithId));

      return lobbyWithId;
    } catch (error) {
      console.error("Error creating game: ", error);
    }
    return null;
  };

  const joinLobby = async (lobbyId: string) => {
    const db = getFirestore();
    const lobbyRef = doc(db, "lobbies", lobbyId);

    try {
      if (!user) {
        throw new Error("User is null");
      }

      const result = await runTransaction(db, async (transaction) => {
        const lobbySnapshot = await transaction.get(lobbyRef);
        const lobbyData = lobbySnapshot.data();

        if (
          !lobbyData ||
          lobbyData.players.length >= 2 ||
          lobbyData.players.includes(user.uid) ||
          // Second verification here because when we loop on all the lobby their state can be changed in the meanwhile
          lobbyData.state !== GameStateEnum.WAITING
        ) {
          // If the lobby is full or the user is already in the lobby, return an error.
          // TODO - let players rejoin lobbies they were disconnected from
          return { status: "error" };
        }

        // Join the associated game.
        let gameSnapshot;
        if (lobbyData && "gameId" in lobbyData) {
          const { gameId } = lobbyData as { gameId: string };
          const gameRef = doc(db, "games", gameId);
          // eslint-disable-next-line fp/no-mutation
          gameSnapshot = await transaction.get(gameRef);
          if (!gameSnapshot.exists()) {
            console.error("Game document does not exist:", gameId);
            return { status: "error" };
          }
        } else {
          throw new Error("Lobby data doesn't contain gameId");
        }

        // Add the user to the lobby and update the lobby state.
        const updatedLobbyData = {
          ...lobbyData,
          players: arrayUnion(user.uid),
          state: GameStateEnum.LIVE,
        };
        transaction.update(lobbyRef, updatedLobbyData);

        // Update the associated game.
        const gameRef = doc(db, "games", lobbyData.gameId);
        const gameData = gameSnapshot.data() as Game;

        // shuffle players to randomize who goes first
        const initialPlayer1 = {
          uid: gameData.players[0],
          currentElo: gameData.player1Elo,
        };
        const initialPlayer2 = {
          uid: user.uid,
          currentElo: user.currentElo || defaultStartingElo,
        };

        const [player1, player2] =
          Math.random() < 0.5
            ? [initialPlayer1, initialPlayer2]
            : [initialPlayer2, initialPlayer1];

        transaction.update(gameRef, {
          players: [player1.uid, player2.uid],
          player1Elo: player1.currentElo,
          player2Elo: player2.currentElo,
          state: GameStateEnum.LIVE,
          currentTurn: 0,
        });

        return {
          status: "success",
        };
      });

      if (result.status === "success") {
        // Fetch the updated lobby and game documents
        const updatedLobbySnapshot = await getDoc(lobbyRef);
        const updatedGameSnapshot = await getDoc(
          doc(db, "games", (updatedLobbySnapshot.data() as Lobby).gameId),
        );
        const updatedLobbyData = updatedLobbySnapshot.data() as Lobby;
        const updatedGameData = updatedGameSnapshot.data() as Game;

        // Check that the user is in the updated lobby and game
        if (
          updatedLobbyData?.players.includes(user.uid) &&
          updatedGameData?.players.includes(user.uid)
        ) {
          dispatch(setLobby({ ...updatedLobbyData, id: lobbyId }));
          dispatch(
            setGame({
              ...updatedGameData,
              id: updatedLobbyData.gameId,
            }),
          );
          return "success";
        }
        console.error("User was not added to the lobby or game");
        return "error";
      }
      return "error";
    } catch (error) {
      console.error("Error joining lobby: ", error);
      return "error";
    }
  };

  const startGame = async (mode: GameModeEnum) => {
    const db = getFirestore();
    // check that no other "waiting" lobby exists where
    // this user was the creator. If there is one, cancel it
    const lobbiesSnapshot = await getDocs(
      query(
        collection(db, "lobbies"),
        where("state", "==", "waiting"),
        where("creator", "==", user?.uid),
      ),
    );
    if (!lobbiesSnapshot.empty) {
      // Clear game state first so the onSnapshot listener won't interfere
      dispatch(setGame(null));
      dispatch(setLobby(null));

      // Cancel all waiting lobbies and their games
      await Promise.all(
        lobbiesSnapshot.docs
          .filter((lobbyDoc) => lobbyDoc.data().state === "waiting")
          .map(async (lobbyDoc) => {
            await updateDoc(doc(db, "lobbies", lobbyDoc.id), {
              state: GameStateEnum.CANCELLED,
            });
            const { gameId } = lobbyDoc.data();
            await updateDoc(doc(db, "games", gameId), {
              state: GameStateEnum.CANCELLED,
            });
          }),
      );
    }

    // Look for a lobby in "waiting" state with the same mode
    const conditions = [
      where("state", "==", "waiting"),
      where("mode", "==", mode),
    ];

    if (mode === GameModeEnum.COMPETITIVE) {
      const elo = user?.currentElo || defaultStartingElo;
      // updae the elo difference based on the user's elo to avoid
      // top/disastrous players waiting forever to find a match
      const updatedEloDifference =
        elo > 800 && elo < 1300 ? ELODifference : ELODifference * 2;
      // eslint-disable-next-line fp/no-mutating-methods
      conditions.push(
        where("creatorElo", "<", elo + updatedEloDifference),
        where("creatorElo", ">", elo - updatedEloDifference),
      );
    }

    const lobbiesWaitingSnapshot = await getDocs(
      query(collection(db, "lobbies"), ...conditions),
    );
    if (!lobbiesWaitingSnapshot.empty) {
      let index = 0;
      while (index < lobbiesWaitingSnapshot.docs.length) {
        const lobbyId = lobbiesWaitingSnapshot.docs[index].id;
        // eslint-disable-next-line no-await-in-loop
        const result = await joinLobby(lobbyId);
        if (result === "success") {
          break; // Exit the loop if joinLobby is successful
        }
        // eslint-disable-next-line fp/no-mutation
        index += 1;
      }
    } else {
      await createLobby(mode);
    }

    navigate("/game");
  };

  if (!user) {
    return <Login message="Please login to compete against other players" />;
  }

  return (
    <div className="GameModeSelection">
      <div className="topWrapper">
        <div className="heroContainer">
          <h1 className="title">Draft</h1>
          <h2 className="headline">Select your favorite game mode</h2>
        </div>
        <div className="GameModeCard">
          <motion.button
            className="joinGameButton"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => startGame(GameModeEnum.COMPETITIVE)}
          >
            Matchmaking
          </motion.button>
          <div>
            <p>Find an opponent and climb the ranks!</p>
            <p>
              Your current elo is <b>{user.currentElo}</b>.
            </p>
          </div>
        </div>
        <div className="FriendlyModeCard">
          <div className="createFriendlyCard">
            <motion.button
              className="joinGameButton"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => {
                // create a lobby then copy the id to clipboard
                // and navigate to the game and warn the user that
                // they need to share the game code with their friend
                createLobby(GameModeEnum.FRIENDLY).then((lobby) => {
                  if (lobby) {
                    const lobbyId = lobby.id || "";
                    navigator.clipboard.writeText(lobbyId);
                    navigate("/game");
                  }
                });
              }}
            >
              Friendly
            </motion.button>
            <div>
              Arrange a friendly match with a friend by sharing your game code.
            </div>
          </div>
          <div className="joinFriendlyCard">
            <div className="joinFriendlyCardButtons">
              <input type="text" name="name" />
              <motion.button
                className="joinLobbyButton"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 1 }}
                onClick={async () => {
                  const inputElem = document?.querySelector("input");
                  if (inputElem) {
                    await joinLobby((inputElem as HTMLInputElement).value);
                    navigate("/game");
                  }
                }}
              >
                <img src={joinIcon} alt="Join Game Icon" />
              </motion.button>
            </div>
            <div>Or paste a game code to join your friend.</div>
          </div>
          {showAds(user) && (
            <Adsense
              client={AdsenseAccount}
              slot="9863584592"
              style={{
                display: "inline-block",
                marginTop: "1em",
                width: "100%",
                height: "100px",
              }}
              format="rectangle"
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default CreateOrJoinGame;
